import React from 'react'
import { saveAs } from 'file-saver'
import {
    AlignmentType,
    BorderStyle,
    Document,
    Packer,
    Paragraph,
    Table,
    TableCell,
    TableRow,
    TextRun,
    WidthType,
} from 'docx'
import MyButton from '../../../../control/button/button'
import { styled } from '@material-ui/core'
import useTranslate from '../../../../i18n/useTranslate'
import { SubmittedTimeline } from '../../../../services/web/useProject'
import TooltipControl from '../../../../control/tooltip/tooltip'
import indexDocumentIcon from '../../../../images/index_document.svg'

export type ExportTimelineIndexButtonProps = {
    projectNo?: string | number
    currentTimeline?: SubmittedTimeline[]
    extraTimeline?: SubmittedTimeline[]
}

const ButtonExport = styled(MyButton)({
    backgroundColor: '#0062FF',
    color: '#fff',
    transition: 'background-color .2s ease, box-shadow .2s ease',
    '&&:hover': { backgroundColor: '#004DE6', boxShadow: '0 2px 8px rgba(0,0,0,.15)' },
    '&&:active': { backgroundColor: '#003FC7', boxShadow: '0 1px 4px rgba(0,0,0,.12)' },
    '&&.Mui-disabled': { backgroundColor: '#B3D1FF', color: '#fff' },
})

const FONT_FAMILY = 'TH SarabunPSK'

const formatTHShort = (input?: string) => {
    if (!input) return ''
    const d = new Date(input)
    if (isNaN(d.getTime())) return ''
    const day = d.getDate()
    const month = d.toLocaleString('th-TH', { month: 'short' })
    const buddhistYY = (d.getFullYear() + 543).toString().slice(-2)
    return `${day} ${month} ${buddhistYY}`
}

const formatENShort = (input?: string) => {
    if (!input) return ''
    const d = new Date(input)
    if (isNaN(d.getTime())) return ''
    const day = d.getDate()
    const month = d.toLocaleString('en-GB', { month: 'short' })
    const yy = d.getFullYear().toString().slice(-2)
    return `${day} ${month} ${yy}`
}

const hasValidDate = (s?: string) => {
    if (!s) return false
    const d = new Date(s)
    return !isNaN(d.getTime())
}

export const ExportTimelineIndexButton: React.FC<ExportTimelineIndexButtonProps> = ({
    projectNo,
    currentTimeline,
    extraTimeline,
}) => {
    const t = useTranslate()
    const isTh = t.languageStore.currentLanguage === 'th'

    const buildHeadRow = React.useCallback(
        () =>
            new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({ text: t.get('EXPORT.DOCX.TIMELINE_INDEX.TABLE.NO'), bold: true }),
                                ],
                            }),
                        ],
                        width: { size: 12, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({ text: t.get('EXPORT.DOCX.TIMELINE_INDEX.TABLE.DATE'), bold: true }),
                                ],
                            }),
                        ],
                        width: { size: 22, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({ text: t.get('EXPORT.DOCX.TIMELINE_INDEX.TABLE.ITEM'), bold: true }),
                                ],
                            }),
                        ],
                        width: { size: 66, type: WidthType.PERCENTAGE },
                    }),
                ],
            }),
        [t],
    )

    const buildDoc = React.useCallback(() => {
        const docChildren: any[] = []

        const projectSlug =
            projectNo != null && String(projectNo).trim() !== ''
                ? String(projectNo)
                : t.get('EXPORT.DOCX.TIMELINE_INDEX.NO_PROJECT') || 'no-project'

        docChildren.push(
            new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                    new TextRun({
                        text: t.get('EXPORT.DOCX.TIMELINE_INDEX.TITLE', [projectSlug]),
                        size: 28,
                        bold: true,
                    }),
                ],
                spacing: { after: 200 },
            }),
        )

        const extraItems = extraTimeline?.flatMap((sub) => (sub?.timelineList ?? []).map((x) => ({ ...x }))) ?? []

        const currentItemsAll =
            currentTimeline?.flatMap((sub) => (sub?.timelineList ?? []).map((x) => ({ ...x }))) ?? []

        let lastDatedIndex = -1
        for (let i = currentItemsAll.length - 1; i >= 0; i--) {
            if (hasValidDate(currentItemsAll[i]?.date)) {
                lastDatedIndex = i
                break
            }
        }

        const currentItems = lastDatedIndex >= 0 ? currentItemsAll.slice(0, lastDatedIndex + 1) : []

        const entries = [...extraItems, ...currentItems]

        const head = buildHeadRow()
        let counter = 1
        const bodyRows = entries.map((titem) => {
            const no = String(counter++)
            const dateText = isTh ? formatTHShort(titem?.date) : formatENShort(titem?.date)
            const itemText = isTh ? (titem?.timelineName?.th ?? '') : (titem?.timelineName?.en ?? '')

            return new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, text: no })],
                        width: { size: 12, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, text: dateText })],
                        width: { size: 22, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, text: itemText })],
                        width: { size: 66, type: WidthType.PERCENTAGE },
                    }),
                ],
            })
        })

        const table = new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [head, ...bodyRows],
            borders: {
                top: { style: BorderStyle.SINGLE, size: 2, color: '000000' },
                bottom: { style: BorderStyle.SINGLE, size: 2, color: '000000' },
                left: { style: BorderStyle.SINGLE, size: 2, color: '000000' },
                right: { style: BorderStyle.SINGLE, size: 2, color: '000000' },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: '000000' },
                insideVertical: { style: BorderStyle.SINGLE, size: 2, color: '000000' },
            },
        })

        docChildren.push(table)

        return new Document({
            styles: {
                default: {
                    document: { run: { font: FONT_FAMILY } },
                },
            },
            sections: [{ properties: {}, children: docChildren }],
        })
    }, [extraTimeline, currentTimeline, isTh, projectNo, t, buildHeadRow])

    const handleExport = async () => {
        const doc = buildDoc()
        const blob = await Packer.toBlob(doc)
        const now = new Date()
        const yyyy = now.getFullYear()
        const mm = String(now.getMonth() + 1).padStart(2, '0')
        const dd = String(now.getDate()).padStart(2, '0')

        const filename = `${t.get('EXPORT.DOCX.TIMELINE_INDEX.FILENAME_PREFIX')}_${projectNo}_${yyyy}-${mm}-${dd}.docx`
        saveAs(blob, filename)
    }

    return (
        <TooltipControl title={t.get('EXPORT.DOCX.TIMELINE_INDEX.EXPORT_BUTTON')} placement="left">
            <div style={{ display: 'flex' }} onClick={handleExport}>
                <img src={indexDocumentIcon} alt="index-document-icon" style={{ height: 40, cursor: 'pointer' }} />
            </div>
        </TooltipControl>
    )
}

export default ExportTimelineIndexButton
